// ✅ src/pages/VehicleV2/VehicleStatusReport_Print.jsx
// FULL WORKING PRINT PAGE (React)
// - reads filters from URL query string (same params as /list)
// - calls: GET https://nissi-india.com/api/vehicleStatusReport/print?...
//   (If you don't have /print API yet, you can point it to /list and return same payload.)
// - prints TWO tables like your ASPX:
//    1) Vehicle Status Report (detail rows)
//    2) Branch Wise Vehicle Report (summary/pivot)
// - includes A4 landscape print CSS + Back + Print buttons

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/vehicleStatusReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (isoOrDate) => {
  if (!isoOrDate) return "";
  // supports "dd/MM/yyyy" already
  if (typeof isoOrDate === "string" && isoOrDate.includes("/")) return isoOrDate;
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return String(isoOrDate);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const fmtMoney = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtNum2 = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Normalize the DETAIL rows to match ASPX columns.
 * Your API may already match these fields. This makes printing stable.
 */
function normalizeDetailRow(x, idx) {
  return {
    sno: idx + 1,
    VehicleType: x?.VehicleType ?? x?.vehicleMainType ?? x?.vehicleMainTypeName ?? "",
    fld_vehicle_code: x?.fld_vehicle_code ?? x?.vehicleCode ?? "",
    fld_vehicle_name: x?.fld_vehicle_name ?? x?.vehicleName ?? "",
    fld_vehicle_model: x?.fld_vehicle_model ?? x?.vehicleModel ?? "",
    fld_vehicle_regno: x?.fld_vehicle_regno ?? x?.regNo ?? "",
    fld_EquipmentSLNO: x?.fld_EquipmentSLNO ?? x?.equipmentSerialNo ?? "",
    fld_purchaseddate: fmtDate(x?.fld_purchaseddate ?? x?.purchaseDate),
    fld_purchased_amt: fmtMoney(x?.fld_purchased_amt ?? x?.purchaseValue),
    fld_cummulativehr: fmtNum2(x?.fld_cummulativehr ?? x?.cumHr),
    fld_cummulativekm: fmtNum2(x?.fld_cummulativekm ?? x?.cumKm),
    fld_progressentrydate: fmtDate(x?.fld_progressentrydate ?? x?.lastUpdate),
    fld_status: x?.fld_status ?? x?.status ?? "",
    fld_vehicle_basistype: x?.fld_vehicle_basistype ?? x?.vehicleBasis ?? "",
    location: x?.location ?? x?.branch ?? "",
  };
}

/**
 * Normalize SUMMARY rows (pivot/branch-wise).
 * We keep dynamic branch columns as-is.
 */
function normalizeSummaryRow(x, idx) {
  return {
    sno: idx + 1,
    VehicleTypes: x?.VehicleTypes ?? "",
    VehicleType: x?.VehicleType ?? "",
    total: x?.total ?? x?.Total ?? "",
    ...x, // keep all dynamic branch columns
  };
}

/* ---------------- component ---------------- */
export default function VehicleStatusReportPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ Read same filters from query string
  // Example print url:
  // /vehicle/VehicleStatusReport_Print?branchType=BRANCH&branchId=8&vehicleMode=O&branch=Branch%208&regNo=&month=March&year=2023&vehicleCode=&vehicleName=&vehicleMainType=&vehicleStatus=
  const params = useMemo(() => {
    const obj = Object.fromEntries([...searchParams.entries()]);
    return {
      branchType: obj.branchType || "BRANCH",
      branchId: obj.branchId || "0",
      vehicleMode: obj.vehicleMode || "O",
      branch: obj.branch || "Select",
      regNo: obj.regNo || "",
      vehicleCode: obj.vehicleCode || "",
      vehicleName: obj.vehicleName || "",
      month: obj.month || "Select",
      year: obj.year || "Select",
      vehicleMainType: obj.vehicleMainType || "Select",
      vehicleStatus: obj.vehicleStatus || "Select",
    };
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [detailRows, setDetailRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        /**
         * ✅ Recommended backend print endpoint:
         * GET /api/vehicleStatusReport/print?...filters...
         * returns:
         * {
         *   success:true,
         *   detailRows:[...],
         *   summaryRows:[...],
         *   count: <detail count>,
         *   totalVehicles: <number>
         * }
         *
         * If you don't have /print yet, you can temporarily call /list
         * AND create summary in backend, or return summaryRows too.
         */
        const res = await axios.get(`${API_BASE}/print/main`, { params });

        const d = res?.data || {};
        const detail = Array.isArray(d.detailRows) ? d.detailRows : Array.isArray(d.rows) ? d.rows : [];
        const summary = Array.isArray(d.summaryRows) ? d.summaryRows : Array.isArray(d.branchWiseRows) ? d.branchWiseRows : [];

        if (!alive) return;

        setDetailRows(detail);
        setSummaryRows(summary);
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [params]);

  const detail = useMemo(() => detailRows.map(normalizeDetailRow), [detailRows]);
  const summary = useMemo(() => summaryRows.map(normalizeSummaryRow), [summaryRows]);

  // ✅ Dynamic branch columns for summary table
  const summaryBranchCols = useMemo(() => {
    if (!summaryRows?.length) return [];
    const reserved = new Set(["sno", "VehicleTypes", "VehicleType", "total", "Total"]);
    const keys = Object.keys(summaryRows[0] || {}).filter((k) => !reserved.has(k));
    // remove keys we already display explicitly
    return keys.filter((k) => !["VehicleTypes", "VehicleType", "total"].includes(k));
  }, [summaryRows]);

  const totalVehicles = useMemo(() => detail.length, [detail.length]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 8px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
          .print-landscape { 
            size: A4 landscape;
            margin: 0.25in;
          }
        }
        @page {
          size: A4 landscape;
          margin: 0.25in;
        }
      `}</style>

      <div className="p-2 print:p-1 print-landscape">
        {/* Top Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-4 border rounded bg-gray-50 text-sm">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-4 border rounded bg-red-50 text-sm text-red-700">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">CORE CONSTRUCTION</div>
              <div className="text-xs">Vehicle Status Report - Print</div>
              <div className="text-[10px] mt-1">
                Mode: <b>{params.vehicleMode === "H" ? "Hire" : "Own"}</b> | Branch: <b>{params.branch}</b> | Month/Year:{" "}
                <b>{params.month}</b> / <b>{params.year}</b>
              </div>
            </div>

            <div className="w-40 text-right text-[10px]">
              <div>INFRA SCREEN : B-48/1</div>
              <div>Total Vehicles: <b>{totalVehicles}</b></div>
            </div>
          </div>
        </div>

        {/* ===================== TABLE 1: Vehicle Status Report ===================== */}
        <div className="text-center mb-1 border-2 border-black py-1">
          <h2 className="text-base font-bold">Vehicle Status Report</h2>
        </div>

        <div className="overflow-x-auto mb-4">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>S.No</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Vehicle Main Type</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Vehicle Code</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 140 }}>Vehicle Name</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Vehicle Model</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Vehicle Registration No</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Equipment Serial No</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 85 }}>Purchase Date</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 85 }}>Purchase Value</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Cummulative Hr</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Cummulative KM</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 85 }}>Last Update</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 95 }}>Current Status</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>Vehicle Basis</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 120 }}>Location</th>
              </tr>
            </thead>

            <tbody>
              {!detail?.length ? (
                <tr>
                  <td colSpan={15} className="border border-black px-2 py-2 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                detail.map((r) => (
                  <tr key={`${r.fld_vehicle_code}-${r.sno}`}>
                    <td className="border border-black px-1 py-1 text-center">{r.sno}</td>
                    <td className="border border-black px-1 py-1">{r.VehicleType}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_vehicle_code}</td>
                    <td className="border border-black px-1 py-1">{r.fld_vehicle_name}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_vehicle_model}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_vehicle_regno}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_EquipmentSLNO}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_purchaseddate}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.fld_purchased_amt}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.fld_cummulativehr}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.fld_cummulativekm}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_progressentrydate}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_status}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.fld_vehicle_basistype}</td>
                    <td className="border border-black px-1 py-1">{r.location}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ===================== TABLE 2: Branch Wise Vehicle Report ===================== */}
        <div className="text-center mb-1 border-2 border-black py-1">
          <h2 className="text-base font-bold">Branch Wise Vehicle Report</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 35 }}>S.No</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 140 }}>Vehicle Main Type</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 140 }}>VehicleType</th>
                <th className="border border-black px-1 py-1 text-center" style={{ minWidth: 60 }}>Total</th>
                {summaryBranchCols.map((c) => (
                  <th key={c} className="border border-black px-1 py-1 text-center" style={{ minWidth: 90 }}>
                    {c}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!summary?.length ? (
                <tr>
                  <td colSpan={4 + summaryBranchCols.length} className="border border-black px-2 py-2 text-center">
                    No summary records found.
                  </td>
                </tr>
              ) : (
                summary.map((r, i) => (
                  <tr key={i}>
                    <td className="border border-black px-1 py-1 text-center">{r.sno}</td>
                    <td className="border border-black px-1 py-1">{r.VehicleTypes}</td>
                    <td className="border border-black px-1 py-1">{r.VehicleType}</td>
                    <td className="border border-black px-1 py-1 text-center">{toStr(r.total)}</td>
                    {summaryBranchCols.map((c) => (
                      <td key={c} className="border border-black px-1 py-1 text-center">
                        {toStr(r[c])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
        </div>

        {/* Bottom Print Button */}
        <div className="mt-4 text-center no-print">
          <button
            onClick={() => window.print()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow-lg"
          >
            🖨️ Print Report
          </button>
        </div>
      </div>
    </div>
  );
}
